import React, {
  useEffect,
  useState,
} from "react";

import {
  FaQuestionCircle,
} from "react-icons/fa";

import {
  toast,
} from "react-toastify";

import EditorDrawerShell from "./EditorDrawerShell";

import {
  Field,
  FormSection,
  Label,
  ToggleCard,
  TUTORIAL_INPUT_CLASS,
} from "./TutorialDrawerFields";

import {
  TUTORIAL_CONTENT_FORMAT_OPTIONS,
} from "../constants/tutorialAdminOptions";

const INITIAL_FORM = {
  slug: "",
  question: "",
  reponse: "",
  format_reponse: "texte",
  categorie: "",
  mots_cles: "",
  roles_cibles: "",
  ordre: 10,
  is_active: true,
};

function rolesToString(roles) {
  if (!roles) {
    return "";
  }

  if (Array.isArray(roles)) {
    return roles.join(", ");
  }

  if (typeof roles === "string") {
    try {
      const parsed =
        JSON.parse(roles);

      return Array.isArray(parsed)
        ? parsed.join(", ")
        : roles;
    } catch {
      return roles;
    }
  }

  return "";
}

function createSlug(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(
      /[\u0300-\u036f]/g,
      ""
    )
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 180);
}

function createInitialForm(
  drawer
) {
  if (
    drawer.mode === "edit" &&
    drawer.data
  ) {
    return {
      slug:
        drawer.data.slug || "",

      question:
        drawer.data.question || "",

      reponse:
        drawer.data.reponse || "",

      format_reponse:
        drawer.data
          .format_reponse ||
        "texte",

      categorie:
        drawer.data.categorie || "",

      mots_cles:
        drawer.data.mots_cles || "",

      roles_cibles:
        rolesToString(
          drawer.data.roles_cibles
        ),

      ordre:
        drawer.data.ordre ?? 10,

      is_active:
        drawer.data.is_active !==
        false,
    };
  }

  return {
    ...INITIAL_FORM,

    ordre:
      drawer.context
        ?.defaultOrder ?? 10,
  };
}

export default function TutorialFaqDrawer({
  drawer,
  saving,
  onClose,
  onSave,
}) {
  const isOpen =
    drawer.open &&
    drawer.type === "faq";

  const [formData, setFormData] =
    useState(INITIAL_FORM);

  const [
    slugManuallyEdited,
    setSlugManuallyEdited,
  ] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setFormData(
      createInitialForm(drawer)
    );

    setSlugManuallyEdited(
      drawer.mode === "edit"
    );
  }, [
    isOpen,
    drawer.mode,
    drawer.data,
    drawer.context,
  ]);

  const handleChange = (event) => {
    const {
      name,
      value,
      type,
      checked,
    } = event.target;

    if (name === "slug") {
      setSlugManuallyEdited(true);
    }

    setFormData((current) => {
      const next = {
        ...current,

        [name]:
          type === "checkbox"
            ? checked
            : value,
      };

      if (
        name === "question" &&
        drawer.mode === "create" &&
        !slugManuallyEdited
      ) {
        next.slug =
          createSlug(value);
      }

      return next;
    });
  };

  const handleSubmit = async (
    event
  ) => {
    event.preventDefault();

    if (!formData.slug.trim()) {
      toast.error(
        "Le slug de la FAQ est obligatoire."
      );
      return;
    }

    if (!formData.question.trim()) {
      toast.error(
        "La question est obligatoire."
      );
      return;
    }

    if (!formData.reponse.trim()) {
      toast.error(
        "La réponse est obligatoire."
      );
      return;
    }

    const order = Number.parseInt(
      formData.ordre,
      10
    );

    if (
      !Number.isInteger(order) ||
      order < 0
    ) {
      toast.error(
        "L’ordre de la FAQ est invalide."
      );
      return;
    }

    const roles =
      formData.roles_cibles
        .split(",")
        .map((role) =>
          role
            .trim()
            .toLowerCase()
        )
        .filter(Boolean);

    await onSave({
      slug: createSlug(
        formData.slug
      ),

      question:
        formData.question.trim(),

      reponse:
        formData.reponse.trim(),

      format_reponse:
        formData.format_reponse,

      categorie:
        formData.categorie
          .trim() || null,

      mots_cles:
        formData.mots_cles
          .trim() || null,

      roles_cibles:
        roles.length
          ? [...new Set(roles)]
          : null,

      ordre: order,

      is_active:
        formData.is_active,
    });
  };

  return (
    <EditorDrawerShell
      open={isOpen}
      eyebrow={
        drawer.mode === "edit"
          ? "Modification"
          : "Nouvelle question"
      }
      title={
        drawer.mode === "edit"
          ? "Modifier la question"
          : "Ajouter une question"
      }
      description="La question sera affichée dans la rubrique Questions fréquentes du tutoriel public."
      icon={
        <FaQuestionCircle />
      }
      saving={saving}
      submitLabel={
        drawer.mode === "edit"
          ? "Enregistrer"
          : "Créer la question"
      }
      onClose={onClose}
      onSubmit={handleSubmit}
    >
      <div className="space-y-6">
        <FormSection
          title="Question et réponse"
          description="Le slug est généré automatiquement depuis la question."
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field className="md:col-span-2">
              <Label required>
                Question
              </Label>

              <input
                type="text"
                maxLength="500"
                className={
                  TUTORIAL_INPUT_CLASS
                }
                name="question"
                value={
                  formData.question
                }
                onChange={handleChange}
                placeholder="Exemple : Je ne reçois pas l’email d’activation"
              />
            </Field>

            <Field>
              <Label required>
                Slug
              </Label>

              <input
                type="text"
                maxLength="180"
                className={
                  TUTORIAL_INPUT_CLASS
                }
                name="slug"
                value={formData.slug}
                onChange={handleChange}
                placeholder="email-activation-non-recu"
              />
            </Field>

            <Field>
              <Label>
                Format de la réponse
              </Label>

              <select
                className={
                  TUTORIAL_INPUT_CLASS
                }
                name="format_reponse"
                value={
                  formData.format_reponse
                }
                onChange={handleChange}
              >
                {TUTORIAL_CONTENT_FORMAT_OPTIONS.map(
                  (option) => (
                    <option
                      key={option.value}
                      value={
                        option.value
                      }
                    >
                      {option.label}
                    </option>
                  )
                )}
              </select>
            </Field>

            <Field className="md:col-span-2">
              <Label required>
                Réponse
              </Label>

              <textarea
                className={`${TUTORIAL_INPUT_CLASS} min-h-[280px] resize-y leading-7`}
                name="reponse"
                value={
                  formData.reponse
                }
                onChange={handleChange}
                placeholder="Saisis la réponse complète..."
              />
            </Field>
          </div>
        </FormSection>

        <FormSection
          title="Classement et recherche"
          description="Ces informations facilitent l’organisation et la recherche dans le tutoriel."
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field>
              <Label>Catégorie</Label>

              <input
                type="text"
                maxLength="150"
                className={
                  TUTORIAL_INPUT_CLASS
                }
                name="categorie"
                value={
                  formData.categorie
                }
                onChange={handleChange}
                placeholder="Compte et connexion"
              />
            </Field>

            <Field>
              <Label>Ordre</Label>

              <input
                type="number"
                min="0"
                step="10"
                className={
                  TUTORIAL_INPUT_CLASS
                }
                name="ordre"
                value={formData.ordre}
                onChange={handleChange}
              />
            </Field>

            <Field className="md:col-span-2">
              <Label>Mots-clés</Label>

              <input
                type="text"
                className={
                  TUTORIAL_INPUT_CLASS
                }
                name="mots_cles"
                value={
                  formData.mots_cles
                }
                onChange={handleChange}
                placeholder="email activation spam connexion"
              />
            </Field>
          </div>
        </FormSection>

        <FormSection
          title="Visibilité"
          description="Laisse les rôles vides pour afficher cette question à tout le monde."
        >
          <Field>
            <Label>Rôles ciblés</Label>

            <input
              type="text"
              className={
                TUTORIAL_INPUT_CLASS
              }
              name="roles_cibles"
              value={
                formData.roles_cibles
              }
              onChange={handleChange}
              placeholder="utilisateur, enseignant, administrateur"
            />

            <p className="mt-2 text-xs leading-5 text-slate-500 dark:text-slate-400">
              Sépare les rôles par des
              virgules.
            </p>
          </Field>

          <div className="mt-5">
            <ToggleCard
              name="is_active"
              checked={
                formData.is_active
              }
              onChange={handleChange}
              title="Question active"
              description="Autorise l’affichage de cette question dans le tutoriel public."
            />
          </div>
        </FormSection>
      </div>
    </EditorDrawerShell>
  );
}